import { globalState } from './global-state';

/**
 * How many BLOCKING dialogs (server validation reports, server error reports) are currently open and
 * waiting on the user.
 *
 * <p>Why this exists: a "please wait" spinner is shown by `waitPatientlySavingWithLock`, whose
 * `finally` cannot run until the awaited action resolves. When the server refuses a request, the api
 * layer surfaces a validation dialog INSIDE that action and waits for the user to dismiss it — so the
 * action is still pending, the spinner appears on top of the dialog, and the user is left staring at a
 * spinner over a message they cannot easily reach. The spinner consults this before showing.</p>
 *
 * <p>Held in globalState (globalThis-backed) rather than a module-local counter for the same reason as
 * `lastApiError`: this module is bundled separately into each entry point (app-index, v6config, ...),
 * so a module-local `let` would give every bundle its own copy — the api layer raising a dialog in one
 * bundle would be invisible to the spinner reading it in another. It also stops the bundler
 * tree-shaking the increment away as a dead store in bundles that only ever write it.</p>
 */
function counter(): number {
  return (globalState().blockingDialogCount as number | undefined) ?? 0;
}

/** True while at least one blocking dialog is waiting on the user. */
export function isBlockingDialogOpen(): boolean {
  return counter() > 0;
}

/**
 * Run <paramref name="show"/> as a blocking dialog, counting it in for its lifetime.
 *
 * A COUNTER rather than a boolean because these can legitimately nest (a validation dialog raised
 * while another is already open); a boolean would be cleared by whichever finished first and would
 * then under-report. `finally` so a throwing dialog cannot leave the count stuck above zero — which
 * would suppress every spinner in the session.
 */
export async function whileBlockingDialogOpen<T>(show: () => Promise<T>): Promise<T> {
  globalState().blockingDialogCount = counter() + 1;
  try {
    return await show();
  } finally {
    globalState().blockingDialogCount = Math.max(0, counter() - 1);
  }
}
